/**
 * Dashboard Config Modal - Configure visible sections, order and width
 */
import React, { useState, useEffect } from 'react';
import { Modal } from '../../components/ui/modal';
import { Switch } from '../../components/ui/switch';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Slider } from '../../components/ui/slider';
import { 
  DollarSign, 
  ShoppingCart, 
  Table2, 
  Wallet, 
  TrendingUp, 
  Clock, 
  AlertTriangle,
  GripVertical,
  Eye,
  EyeOff,
  Calendar,
  Columns,
} from 'lucide-react';

// Default sections configuration with width support (1-4 columns)
export const DEFAULT_SECTIONS = [
  { id: 'stats', name: 'Resumo do Dia', icon: DollarSign, enabled: true, order: 0, width: 4 },
  { id: 'expiryAlerts', name: 'Alertas de Validade', icon: Calendar, enabled: true, order: 1, width: 2 },
  { id: 'stockAlerts', name: 'Alertas de Estoque', icon: AlertTriangle, enabled: true, order: 2, width: 2 },
  { id: 'hourlyChart', name: 'Vendas por Hora', icon: TrendingUp, enabled: true, order: 3, width: 2 },
  { id: 'paymentChart', name: 'Vendas por Pagamento', icon: Wallet, enabled: true, order: 4, width: 2 },
  { id: 'topProducts', name: 'Produtos Mais Vendidos', icon: TrendingUp, enabled: true, order: 5, width: 2 },
  { id: 'recentSales', name: 'Vendas Recentes', icon: Clock, enabled: true, order: 6, width: 2 },
];

// Local storage key
const STORAGE_KEY = 'dashboard_config_v2';

// Load config from localStorage
export function loadDashboardConfig() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Merge with defaults (in case new sections are added)
      return DEFAULT_SECTIONS.map(section => {
        const savedSection = parsed.find(s => s.id === section.id);
        return savedSection 
          ? { ...section, enabled: savedSection.enabled, order: savedSection.order, width: savedSection.width ?? section.width } 
          : section;
      }).sort((a, b) => a.order - b.order);
    }
  } catch (e) {
    console.error('Error loading dashboard config:', e);
  }
  return DEFAULT_SECTIONS;
}

// Save config to localStorage
export function saveDashboardConfig(config) {
  try {
    const toSave = config.map(({ id, enabled, order, width }) => ({ id, enabled, order, width }));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch (e) {
    console.error('Error saving dashboard config:', e);
  }
}

const widthLabels = {
  1: '1 coluna (25%)',
  2: '2 colunas (50%)',
  3: '3 colunas (75%)',
  4: '4 colunas (100%)',
};

export function DashboardConfigModal({ open, onOpenChange, config, onSave }) {
  const [localConfig, setLocalConfig] = useState(config);
  const [draggedItem, setDraggedItem] = useState(null);

  useEffect(() => {
    setLocalConfig(config);
  }, [config, open]);

  const toggleSection = (id) => {
    setLocalConfig(prev => 
      prev.map(s => s.id === id ? { ...s, enabled: !s.enabled } : s)
    );
  };

  const setWidth = (id, width) => {
    setLocalConfig(prev =>
      prev.map(s => s.id === id ? { ...s, width } : s)
    );
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;
    
    const newConfig = [...localConfig];
    const item = newConfig[draggedItem];
    newConfig.splice(draggedItem, 1);
    newConfig.splice(index, 0, item);
    
    // Update order
    newConfig.forEach((s, i) => s.order = i);
    
    setLocalConfig(newConfig);
    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const moveUp = (index) => {
    if (index === 0) return;
    const newConfig = [...localConfig];
    [newConfig[index - 1], newConfig[index]] = [newConfig[index], newConfig[index - 1]];
    newConfig.forEach((s, i) => s.order = i);
    setLocalConfig(newConfig);
  };

  const moveDown = (index) => {
    if (index === localConfig.length - 1) return;
    const newConfig = [...localConfig];
    [newConfig[index], newConfig[index + 1]] = [newConfig[index + 1], newConfig[index]];
    newConfig.forEach((s, i) => s.order = i);
    setLocalConfig(newConfig);
  };

  const handleSave = () => {
    onSave(localConfig);
    onOpenChange(false);
  };

  const handleReset = () => {
    setLocalConfig(DEFAULT_SECTIONS);
  };

  const enabledCount = localConfig.filter(s => s.enabled).length;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Configurar Dashboard"
      description="Escolha quais seções exibir, a ordem e a largura de cada uma"
      size="lg"
      onConfirm={handleSave}
      onCancel={() => onOpenChange(false)}
      confirmText="Salvar"
      cancelText="Cancelar"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={handleReset}>
            Restaurar Padrão
          </Button>
          <div className="flex-1" />
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleSave}>
            Salvar
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Arraste para reordenar ou use os botões</span>
          <Badge variant="secondary">{enabledCount} de {localConfig.length} visíveis</Badge>
        </div>

        <div className="space-y-3">
          {localConfig.map((section, index) => {
            const Icon = section.icon;
            return (
              <div
                key={section.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`
                  rounded-lg border transition-all
                  ${section.enabled ? 'bg-card' : 'bg-muted/50 opacity-60'}
                  ${draggedItem === index ? 'ring-2 ring-primary' : ''}
                  cursor-grab active:cursor-grabbing
                `}
              >
                {/* Header row */}
                <div className="flex items-center gap-3 p-3">
                  <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      section.enabled ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                    }`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium truncate ${!section.enabled && 'text-muted-foreground'}`}>
                        {section.name}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="flex gap-1">
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7"
                        onClick={() => moveUp(index)}
                        disabled={index === 0}
                      >
                        ↑
                      </Button>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7"
                        onClick={() => moveDown(index)}
                        disabled={index === localConfig.length - 1}
                      >
                        ↓
                      </Button>
                    </div>
                    
                    <Switch
                      checked={section.enabled}
                      onCheckedChange={() => toggleSection(section.id)}
                    />
                  </div>
                </div>

                {/* Width control - only show when enabled */}
                {section.enabled && (
                  <div className="px-3 pb-3 pt-0">
                    <div className="flex items-center gap-3 pl-7">
                      <Columns className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      <span className="text-xs text-muted-foreground w-24">Largura:</span>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4].map(w => (
                          <Button
                            key={w}
                            type="button"
                            variant={section.width === w ? 'default' : 'outline'}
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => setWidth(section.id, w)}
                          >
                            {w}
                          </Button>
                        ))}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {widthLabels[section.width] || widthLabels[2]}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm">
          <p className="font-medium text-blue-600 mb-1">Dica</p>
          <p className="text-muted-foreground">
            As configurações são salvas localmente. O dashboard usa um grid de 4 colunas - ajuste a largura de cada seção para personalizar o layout.
          </p>
        </div>
      </div>
    </Modal>
  );
}

export default DashboardConfigModal;
